import * as React from 'react';
import { Backspace, Enter, Escape, PageDown, PageUp, Space } from '@fluentui/keyboard-keys';
import { useControllableState } from '@fluentui/react-utilities';
import { useFluent_unstable as useFluent } from '@fluentui/react-shared-contexts';
import { addMonths, addYears, DateRangeType, DayOfWeek, DEFAULT_CALENDAR_STRINGS, DEFAULT_DATE_FORMATTING, FirstWeekOfYear, focusAsync } from '../../utils';
import { CalendarDay } from '../CalendarDay/CalendarDay';
import { CalendarMonth } from '../CalendarMonth/CalendarMonth';
import { defaultNavigationIcons } from './calendarNavigationIcons';
import { useCalendarStyles_unstable } from './useCalendarStyles.styles';
const MIN_SIZE_FORCE_OVERLAY = 440;
const defaultWorkWeekDays = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday
];
function useDateState(props) {
    const { value, today: todayProp, onSelectDate } = props;
    const today = React.useMemo(()=>todayProp !== null && todayProp !== void 0 ? todayProp : new Date(), [
        todayProp
    ]);
    /** The currently selected date in the calendar */ const [selectedDate, setSelectedDate] = useControllableState({
        state: value,
        defaultState: value ? undefined : today,
        initialState: today
    });
    /** The currently focused date in the day picker, but not necessarily selected */ const [navigatedDay = today, setNavigatedDay] = React.useState(value);
    /** The currently focused date in the month picker, but not necessarily selected */ const [navigatedMonth = today, setNavigatedMonth] = React.useState(value);
    /** If using a controlled value, when that value changes, navigate to that date */ const [lastSelectedDate = today, setLastSelectedDate] = React.useState(value);
    if (value && lastSelectedDate.valueOf() !== value.valueOf()) {
        setNavigatedDay(value);
        setNavigatedMonth(value);
        setLastSelectedDate(value);
    }
    const navigateMonth = (date)=>{
        setNavigatedMonth(date);
    };
    const navigateDay = (date)=>{
        setNavigatedMonth(date);
        setNavigatedDay(date);
    };
    const onDateSelected = (date, selectedDateRangeArray)=>{
        setNavigatedMonth(date);
        setNavigatedDay(date);
        setSelectedDate(date);
        onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(date, selectedDateRangeArray);
    };
    return [
        selectedDate,
        navigatedDay,
        navigatedMonth,
        onDateSelected,
        navigateDay,
        navigateMonth
    ];
}
function useVisibilityState({ isDayPickerVisible: isDayPickerVisibleProp, isMonthPickerVisible: isMonthPickerVisibleProp, showMonthPickerAsOverlay }) {
    /** State used to show/hide month picker */ const showMonthPickerAsOverlayState = useShowMonthPickerAsOverlay({
        isDayPickerVisible: isDayPickerVisibleProp,
        showMonthPickerAsOverlay
    });
    const [isMonthPickerVisible, setIsMonthPickerVisible] = React.useState(()=>showMonthPickerAsOverlayState ? false : isMonthPickerVisibleProp !== null && isMonthPickerVisibleProp !== void 0 ? isMonthPickerVisibleProp : false);
    /** State used to show/hide day picker */ const [isDayPickerVisible, setIsDayPickerVisible] = React.useState(()=>showMonthPickerAsOverlayState ? true : isDayPickerVisibleProp !== null && isDayPickerVisibleProp !== void 0 ? isDayPickerVisibleProp : true);
    const toggleDayMonthPickerVisibility = ()=>{
        setIsMonthPickerVisible(!isMonthPickerVisible);
        setIsDayPickerVisible(!isDayPickerVisible);
    };
    return [
        isMonthPickerVisible,
        isDayPickerVisible,
        toggleDayMonthPickerVisibility
    ];
}
function useFocusLogic({ componentRef }, isDayPickerVisible, isMonthPickerVisible) {
    const dayPicker = React.useRef(null);
    const monthPicker = React.useRef(null);
    const focusOnUpdate = React.useRef(false);
    const { targetDocument } = useFluent();
    const win = targetDocument === null || targetDocument === void 0 ? void 0 : targetDocument.defaultView;
    const focus = React.useCallback(()=>{
        if (isDayPickerVisible && dayPicker.current) {
            focusAsync(dayPicker.current, win);
        } else if (isMonthPickerVisible && monthPicker.current) {
            focusAsync(monthPicker.current, win);
        }
    }, [
        isDayPickerVisible,
        isMonthPickerVisible,
        win
    ]);
    React.useImperativeHandle(componentRef, ()=>({
            focus
        }), [
        focus
    ]);
    React.useEffect(()=>{
        if (focusOnUpdate.current) {
            focus();
            focusOnUpdate.current = false;
        }
    });
    const focusOnNextUpdate = ()=>{
        focusOnUpdate.current = true;
    };
    return [
        dayPicker,
        monthPicker,
        focusOnNextUpdate
    ];
}
/**
 * @internal
 */ export const Calendar = /*#__PURE__*/ React.forwardRef((props, forwardedRef)=>{
    const { allFocusable = false, calendarDayProps, calendarMonthProps, className, componentRef, dateRangeType = DateRangeType.Day, dateTimeFormatter = DEFAULT_DATE_FORMATTING, firstDayOfWeek = DayOfWeek.Sunday, firstWeekOfYear = FirstWeekOfYear.FirstDay, highlightCurrentMonth = false, highlightSelectedMonth = false, id, isDayPickerVisible: isDayPickerVisibleProp = true, isMonthPickerVisible: isMonthPickerVisibleProp = true, maxDate, minDate, onDismiss, onSelectDate, restrictedDates, showCloseButton = false, showGoToToday = true, showMonthPickerAsOverlay: showMonthPickerAsOverlayProp = false, showSixWeeksByDefault = false, showWeekNumbers = false, strings = DEFAULT_CALENDAR_STRINGS, today: todayProp, value, workWeekDays = defaultWorkWeekDays } = props;
    const today = React.useMemo(()=>{
        return todayProp !== null && todayProp !== void 0 ? todayProp : new Date();
    }, [
        todayProp
    ]);
    const [selectedDate, navigatedDay, navigatedMonth, onDateSelected, navigateDay, navigateMonth] = useDateState({
        onSelectDate,
        value,
        today
    });
    const [isMonthPickerVisible, isDayPickerVisible, toggleDayMonthPickerVisibility] = useVisibilityState({
        isDayPickerVisible: isDayPickerVisibleProp,
        isMonthPickerVisible: isMonthPickerVisibleProp,
        showMonthPickerAsOverlay: showMonthPickerAsOverlayProp
    });
    const [dayPicker, monthPicker, focusOnNextUpdate] = useFocusLogic({
        componentRef
    }, isDayPickerVisible, isMonthPickerVisible);
    const renderGoToTodayButton = ()=>{
        let goTodayEnabled = showGoToToday;
        if (goTodayEnabled && today) {
            goTodayEnabled = navigatedDay.getFullYear() !== today.getFullYear() || navigatedDay.getMonth() !== today.getMonth() || navigatedMonth.getFullYear() !== today.getFullYear() || navigatedMonth.getMonth() !== today.getMonth();
        }
        return showGoToToday && /*#__PURE__*/ React.createElement("button", {
            className: classes.goTodayButton,
            onClick: onGotoToday,
            onKeyDown: onButtonKeyDown(onGotoToday),
            type: "button",
            disabled: !goTodayEnabled
        }, strings.goToToday);
    };
    const onNavigateDayDate = (date, focusOnNavigatedDay)=>{
        navigateDay(date);
        if (focusOnNavigatedDay) {
            focusOnNextUpdate();
        }
    };
    const onNavigateMonthDate = (date, focusOnNavigatedDay)=>{
        if (focusOnNavigatedDay) {
            focusOnNextUpdate();
        }
        if (!focusOnNavigatedDay) {
            navigateMonth(date);
            return;
        }
        if (monthPickerOnly) {
            onDateSelected(date);
        }
        navigateDay(date);
    };
    const showMonthPickerAsOverlay = useShowMonthPickerAsOverlay({
        isDayPickerVisible: isDayPickerVisibleProp,
        showMonthPickerAsOverlay: showMonthPickerAsOverlayProp
    });
    const onHeaderSelect = showMonthPickerAsOverlay ? ()=>{
        toggleDayMonthPickerVisibility();
        focusOnNextUpdate();
    } : undefined;
    const onGotoToday = ()=>{
        navigateDay(today);
        if (showMonthPickerAsOverlay && isMonthPickerVisible) {
            toggleDayMonthPickerVisibility();
        }
        focusOnNextUpdate();
    };
    const onButtonKeyDown = (callback)=>{
        return (ev)=>{
            switch(ev.key){
                case Enter:
                case Space:
                    callback();
                    break;
            }
        };
    };
    const onDatePickerPopupKeyDown = (ev)=>{
        switch(ev.key){
            case Enter:
                ev.preventDefault();
                break;
            case Backspace:
                ev.preventDefault();
                break;
            case Escape:
                ev.stopPropagation();
                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
                break;
            case PageUp:
                if (ev.ctrlKey) {
                    // go to next year
                    navigateDay(addYears(navigatedDay, 1));
                } else {
                    // go to next month
                    navigateDay(addMonths(navigatedDay, 1));
                }
                ev.preventDefault();
                break;
            case PageDown:
                if (ev.ctrlKey) {
                    // go to previous year
                    navigateDay(addYears(navigatedDay, -1));
                } else {
                    // go to previous month
                    navigateDay(addMonths(navigatedDay, -1));
                }
                ev.preventDefault();
                break;
            default:
                break;
        }
    };
    const monthPickerOnly = !showMonthPickerAsOverlay && !isDayPickerVisible;
    const classes = useCalendarStyles_unstable({
        className,
        isDayPickerVisible,
        isMonthPickerVisible,
        showWeekNumbers
    });
    let todayDateString = '';
    let selectedDateString = '';
    if (dateTimeFormatter && strings.todayDateFormatString) {
        todayDateString = strings.todayDateFormatString.replace('{0}', dateTimeFormatter.formatMonthDayYear(today, strings));
    }
    if (dateTimeFormatter && strings.selectedDateFormatString) {
        const dateStringFormatter = monthPickerOnly ? dateTimeFormatter.formatMonthYear : dateTimeFormatter.formatMonthDayYear;
        selectedDateString = strings.selectedDateFormatString.replace('{0}', dateStringFormatter(selectedDate, strings));
    }
    const selectionAndTodayString = selectedDateString + ', ' + todayDateString;
    return /*#__PURE__*/ React.createElement("div", {
        id: id,
        ref: forwardedRef,
        className: classes.root,
        onKeyDown: onDatePickerPopupKeyDown
    }, /*#__PURE__*/ React.createElement("div", {
        className: classes.liveRegion,
        "aria-live": "polite",
        "aria-atomic": "true"
    }, /*#__PURE__*/ React.createElement("span", null, selectedDateString)), isDayPickerVisible && /*#__PURE__*/ React.createElement(CalendarDay, {
        gridLabel: selectionAndTodayString,
        selectedDate: selectedDate,
        navigatedDate: navigatedDay,
        today: today,
        onSelectDate: onDateSelected,
        // eslint-disable-next-line react/jsx-no-bind
        onNavigateDate: onNavigateDayDate,
        onDismiss: onDismiss,
        firstDayOfWeek: firstDayOfWeek,
        dateRangeType: dateRangeType,
        strings: strings,
        // eslint-disable-next-line react/jsx-no-bind
        onHeaderSelect: onHeaderSelect,
        showWeekNumbers: showWeekNumbers,
        firstWeekOfYear: firstWeekOfYear,
        dateTimeFormatter: dateTimeFormatter,
        showSixWeeksByDefault: showSixWeeksByDefault,
        minDate: minDate,
        maxDate: maxDate,
        navigationIcons: defaultNavigationIcons,
        restrictedDates: restrictedDates,
        workWeekDays: workWeekDays,
        componentRef: dayPicker,
        showCloseButton: showCloseButton,
        allFocusable: allFocusable,
        ...calendarDayProps
    }), isDayPickerVisible && isMonthPickerVisible && /*#__PURE__*/ React.createElement("div", {
        className: classes.divider
    }), isMonthPickerVisible ? /*#__PURE__*/ React.createElement("div", {
        className: classes.monthPickerWrapper
    }, /*#__PURE__*/ React.createElement(CalendarMonth, {
        navigatedDate: navigatedMonth,
        selectedDate: navigatedDay,
        strings: strings,
        // eslint-disable-next-line react/jsx-no-bind
        onNavigateDate: onNavigateMonthDate,
        today: today,
        highlightCurrentMonth: highlightCurrentMonth,
        highlightSelectedMonth: highlightSelectedMonth,
        // eslint-disable-next-line react/jsx-no-bind
        onHeaderSelect: onHeaderSelect,
        dateTimeFormatter: dateTimeFormatter,
        minDate: minDate,
        maxDate: maxDate,
        componentRef: monthPicker,
        navigationIcons: defaultNavigationIcons,
        ...calendarMonthProps
    }), renderGoToTodayButton()) : renderGoToTodayButton());
});
Calendar.displayName = 'Calendar';
const useShowMonthPickerAsOverlay = ({ isDayPickerVisible, showMonthPickerAsOverlay })=>{
    const { targetDocument } = useFluent();
    const win = targetDocument === null || targetDocument === void 0 ? void 0 : targetDocument.defaultView;
    return showMonthPickerAsOverlay || isDayPickerVisible && win && win.innerWidth <= MIN_SIZE_FORCE_OVERLAY;
};
